/**
 * Client-side rate limiting utility for enhanced security
 * Prevents rapid-fire login attempts and other abuse
 */

interface RateLimitEntry {
  attempts: number;
  lastAttempt: number;
  blockedUntil?: number;
}

class RateLimiter {
  private storage: Map<string, RateLimitEntry> = new Map();
  private readonly maxAttempts: number;
  private readonly windowMs: number;
  private readonly blockDurationMs: number;

  constructor(
    maxAttempts: number = 5,
    windowMs: number = 15 * 60 * 1000, // 15 minutes
    blockDurationMs: number = 30 * 60 * 1000 // 30 minutes
  ) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
    this.blockDurationMs = blockDurationMs;
    
    // Clean up old entries periodically
    setInterval(() => this.cleanup(), 5 * 60 * 1000); // Every 5 minutes
  }

  /**
   * Check if an action is allowed for the given key
   */
  isAllowed(key: string): boolean {
    const now = Date.now();
    const entry = this.storage.get(key);

    if (!entry) {
      return true;
    }

    // Check if still blocked
    if (entry.blockedUntil && now < entry.blockedUntil) {
      return false;
    }

    // Reset if window has passed
    if (now - entry.lastAttempt > this.windowMs) {
      this.storage.delete(key);
      return true;
    }

    return entry.attempts < this.maxAttempts;
  }

  /**
   * Record an attempt for the given key
   */
  recordAttempt(key: string, success: boolean = false): void {
    const now = Date.now();
    const entry = this.storage.get(key) || { attempts: 0, lastAttempt: now };

    if (success) {
      // Reset on successful attempt
      this.storage.delete(key);
      return;
    }

    // Increment failed attempts
    entry.attempts += 1;
    entry.lastAttempt = now;

    // Block if max attempts reached
    if (entry.attempts >= this.maxAttempts) {
      entry.blockedUntil = now + this.blockDurationMs;
    }

    this.storage.set(key, entry);
  }

  /**
   * Get remaining time until unblocked (in milliseconds)
   */
  getBlockedTime(key: string): number {
    const entry = this.storage.get(key);
    if (!entry || !entry.blockedUntil) {
      return 0;
    }

    const remaining = entry.blockedUntil - Date.now();
    return Math.max(0, remaining);
  }

  /**
   * Get remaining attempts before blocking
   */
  getRemainingAttempts(key: string): number {
    const entry = this.storage.get(key);
    if (!entry) {
      return this.maxAttempts;
    }

    return Math.max(0, this.maxAttempts - entry.attempts);
  }

  /**
   * Clean up old entries
   */
  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.storage.entries()) {
      // Remove entries older than window + block duration
      const maxAge = this.windowMs + this.blockDurationMs;
      if (now - entry.lastAttempt > maxAge) {
        this.storage.delete(key);
      }
    }
  }

  /**
   * Clear all entries (for testing or admin purposes)
   */
  clear(): void {
    this.storage.clear();
  }
}

// Create singleton instances for different types of actions
export const loginRateLimiter = new RateLimiter(5, 15 * 60 * 1000, 30 * 60 * 1000); // 5 attempts per 15 min, block for 30 min
export const passwordResetRateLimiter = new RateLimiter(3, 60 * 60 * 1000, 60 * 60 * 1000); // 3 attempts per hour, block for 1 hour

/**
 * Get user identifier for rate limiting (IP-based fallback if no user)
 */
export function getRateLimitKey(email?: string): string {
  if (email) {
    return `user:${email.toLowerCase()}`;
  }
  
  // Fallback to a browser fingerprint
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset()
  ].join('|');
  
  return `browser:${btoa(fingerprint).slice(0, 16)}`;
}

/**
 * Format remaining time for user display
 */
export function formatBlockedTime(milliseconds: number): string {
  if (milliseconds <= 0) return '';
  
  const minutes = Math.ceil(milliseconds / (60 * 1000));
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  
  const hours = Math.ceil(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}
